import React, { useState, useEffect, useRef } from "react";
import { CircularProgress, IconButton } from "@mui/material";
import { Mic, Pause } from "@mui/icons-material";
import axiosServices from "../../../services/apiClient";

const AudioRecorder = ({
  setInputValue,
  isRecording,
  setIsRecording,
  handleRecord,
  transcriptionLanguage = "mr",
}) => {
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [loading, setLoading] = useState(false);
  const transcriptionLanguageRef = useRef(transcriptionLanguage);
  useEffect(() => {
    transcriptionLanguageRef.current = transcriptionLanguage;
  }, [transcriptionLanguage]);

  useEffect(() => {
    // Initialize MediaRecorder
    if (isRecording) {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          const recorder = new MediaRecorder(stream);
          setMediaRecorder(recorder);
          recorder.start();

          const audioChunks = [];
          recorder.ondataavailable = (event) => {
            audioChunks.push(event.data);
          };

          recorder.onstop = async () => {
            setLoading(true);
            const audioBlob = new Blob(audioChunks, { type: "audio/wav" });
            const formData = new FormData();
            formData.append("file", audioBlob);

            try {
              const response = await axiosServices.post(
                `/audio/transcribe`,
                formData,
                {
                  headers: { "Content-Type": "multipart/form-data" },
                  params: { language: transcriptionLanguageRef.current },
                },
              );

              setInputValue(response.data.transcription); // Assuming the transcription text is in `response.data.transcription`
            } catch (error) {
              console.error("Error transcribing audio:", error);
            } finally {
              setLoading(false);
            }
          };
        })
        .catch((error) => {
          console.error("Microphone access denied:", error);
          setIsRecording(false);
        });
    } else if (mediaRecorder) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach((track) => track.stop());
    }
  }, [isRecording]);

  return (
    <IconButton
      onClick={handleRecord}
      disabled={loading}
      display={isRecording ? "none" : "flex"}
      aria-label="audio input"
      sx={{
        width: "44px",
        height: "44px",
        borderRadius: "12px",
        backgroundColor: "rgba(24, 116, 210, 0.08)",
        color: "rgba(24, 116, 210, 1)",
        border: "1px solid rgba(24, 116, 210, 0.12)",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          backgroundColor: "rgba(24, 116, 210, 0.12)",
          transform: "translateY(-1px)",
          boxShadow: "0 4px 12px rgba(24, 116, 210, 0.2)",
        },
        "&:disabled": {
          backgroundColor: "rgba(24, 116, 210, 0.04)",
          color: "rgba(24, 116, 210, 0.4)",
          transform: "none",
        },
      }}
    >
      {loading ? (
        <CircularProgress size={20} />
      ) : isRecording ? (
        <Pause sx={{ fontSize: "20px" }} />
      ) : (
        <Mic sx={{ fontSize: "20px" }} />
      )}
    </IconButton>
  );
};

export default AudioRecorder;
